import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import { usePageContent } from "@/hooks/usePageContent";

const steps = [
  {
    num: "01",
    icon: "ri-map-pin-2-line",
    title: "Submit Property",
    duration: "4 minutes",
    desc: "Share your land's GPS location or address, approximate size, ownership type, and upload relevant documents. Our secure portal encrypts everything. The entire submission takes under 4 minutes.",
    details: ["GPS coordinates or address", "Land size (sq.ft / acres)", "Ownership type (self/inherited/joint)", "Legal documents & encumbrance status", "State / district / taluka info"],
    image: "https://readdy.ai/api/search-image?query=Person%20using%20laptop%20to%20fill%20online%20land%20submission%20form%2C%20modern%20dark%20workspace%2C%20map%20with%20GPS%20pin%20on%20screen%2C%20cinematic%20moody%20lighting%2C%20teal%20and%20amber%20accent%20glow%2C%20professional%20real%20estate%20technology%20concept%2C%20India%20setting&width=700&height=450&seq=hiw-step1&orientation=landscape",
  },
  {
    num: "02",
    icon: "ri-brain-line",
    title: "AI Analysis",
    duration: "48 hours",
    desc: "Our HBU Engine ingests your data and cross-references it with 40+ live data feeds — zoning databases, RERA records, brand expansion plans, footfall data, infrastructure projects, and comparable transaction prices.",
    details: ["Zoning & FSI compliance check", "Catchment area footfall analysis", "Comparable transaction pricing", "Infrastructure pipeline overlay", "Brand match scoring"],
    image: "https://readdy.ai/api/search-image?query=AI%20data%20visualization%20dashboard%20on%20multiple%20screens%2C%20glowing%20green%20neural%20network%20and%20map%20analysis%2C%20dark%20tech%20environment%2C%20cinematic%20moody%20atmosphere%2C%20property%20analytics%20interface%2C%20teal%20neon%20glow%2C%20futuristic%20real%20estate%20AI%20technology&width=700&height=450&seq=hiw-step2&orientation=landscape",
  },
  {
    num: "03",
    icon: "ri-file-chart-line",
    title: "Highest & Best Use",
    duration: "Delivered in 48 hours",
    desc: "You receive a comprehensive analysis identifying the top 3 uses for your land, ranked by return potential and feasibility. Each option includes market demand scoring, regulatory fit, and estimated timeline to monetization.",
    details: ["Top 3 uses ranked by ROI", "Market demand & feasibility scoring", "Regulatory & zoning fit analysis", "Estimated timeline to monetization", "Risk-adjusted return projections"],
    image: "https://readdy.ai/api/search-image?query=Professional%20real%20estate%20AI%20feasibility%20report%20document%20on%20desk%2C%20charts%20and%20analytics%20pages%20visible%2C%20dark%20moody%20office%20setting%2C%20warm%20desk%20lamp%20glow%2C%20cinematic%20business%20photography%2C%20teal%20and%20amber%20color%20palette%2C%20India%20professional%20setting&width=700&height=450&seq=hiw-step3&orientation=landscape",
  },
  {
    num: "04",
    icon: "ri-bar-chart-grouped-line",
    title: "ROI Projection",
    duration: "2–4 weeks",
    desc: "Detailed financial modeling including Capex breakdown, revenue forecasts, IRR calculations, payback period analysis, and exit scenarios. Compare multiple strategies side-by-side with sensitivity analysis.",
    details: ["Capex & revenue forecasting", "IRR and payback period analysis", "Multiple exit scenario modeling", "Sensitivity & risk analysis", "5-year financial projections"],
    image: "https://readdy.ai/api/search-image?query=Financial%20ROI%20projection%20charts%20and%20spreadsheets%20on%20large%20monitor%2C%20dark%20modern%20office%2C%20glowing%20data%20visualizations%2C%20teal%20and%20amber%20accent%20lighting%2C%20cinematic%20business%20analytics%20environment%2C%20India%20professional%20setting&width=700&height=450&seq=hiw-step4&orientation=landscape",
  },
  {
    num: "05",
    icon: "ri-megaphone-line",
    title: "Marketing Strategy",
    duration: "2–6 weeks",
    desc: "We build a targeted marketing plan — investor profiling, creative assets, channel selection (digital + offline), and outreach sequencing. Your land gets in front of the right buyers, developers, and brands at the right time.",
    details: ["Investor & buyer targeting", "Creative assets & pitch decks", "Digital & offline channel plan", "Outreach sequencing & follow-ups", "Lead qualification & scoring"],
    image: "https://readdy.ai/api/search-image?query=Real%20estate%20marketing%20strategy%20presentation%20with%20charts%20and%20creative%20mockups%20on%20screen%2C%20dark%20modern%20office%2C%20strategic%20planning%20session%2C%20teal%20and%20amber%20accent%20lighting%2C%20cinematic%20business%20photography%2C%20India%20professional%20setting&width=700&height=450&seq=hiw-step5&orientation=landscape",
  },
  {
    num: "06",
    icon: "ri-shake-hands-line",
    title: "Deal Closure",
    duration: "4–12 weeks",
    desc: "Our team handles negotiation, legal structuring, documentation, and execution support. From term sheets to stamp duty — we ensure your deal closes smoothly with your interests fully protected.",
    details: ["Negotiation & term sheet finalization", "Legal agreement drafting & review", "Due diligence & title verification", "Documentation & registration support", "Post-deal execution monitoring"],
    image: "https://readdy.ai/api/search-image?query=Business%20meeting%20handshake%20between%20real%20estate%20developer%20and%20landowner%2C%20modern%20dark%20office%20environment%2C%20documents%20and%20site%20plans%20on%20table%2C%20cinematic%20moody%20lighting%2C%20teal%20and%20amber%20accent%20light%2C%20India%20professional%20business%20setting&width=700&height=450&seq=hiw-step6&orientation=landscape",
  },
];

export default function HowItWorksPage() {
  const { getContent } = usePageContent('how-it-works');
  const ctaHeading = getContent('hiw-cta-heading') || 'Ready to Start Your Journey?';
  const ctaSub = getContent('hiw-cta-sub') || 'The first step is a 4-minute form. We\'ll handle everything else.';

  return (
    <PageLayout>
      <PageHero
        badge="AI Property Analysis"
        title="From submission to closure, in one"
        highlight="intelligent flow"
        subtitle="A transparent 6-step journey powered by AI — from property submission to deal closure."
        image="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20idle%20land%20plot%20transforming%20into%20modern%20developed%20property%2C%20split%20before-after%20concept%2C%20dramatic%20golden%20hour%20lighting%2C%20dark%20cinematic%20atmosphere%2C%20India%20real%20estate%20transformation%2C%20teal%20and%20amber%20color%20grading%2C%20highly%20detailed&width=1800&height=800&seq=hiw-hero-page&orientation=landscape"
      />

      {/* Timeline steps */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="space-y-16">
            {steps.map((step, i) => (
              <div
                key={step.num}
                className={`grid md:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
              >
                {/* Image */}
                <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                  <img src={step.image} alt={step.title} className="w-full h-full object-cover object-top" />
                </div>
                {/* Content */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-5xl font-display font-bold text-foreground-900/20">{step.num}</span>
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30">
                      <i className={`${step.icon} text-xl text-primary-500`} />
                    </div>
                  </div>
                  <div className="inline-block text-xs font-medium px-3 py-1 rounded-full bg-primary-500/10 text-primary-700 mb-3">
                    {step.duration}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-4">{step.title}</h2>
                  <p className="text-foreground-600 leading-relaxed mb-5">{step.desc}</p>
                  <ul className="space-y-2">
                    {step.details.map((d) => (
                      <li key={d} className="flex items-center gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-primary-500 shrink-0" /> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            {ctaHeading}
          </h2>
          <p className="text-foreground-600 mb-8">
            {ctaSub}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get Started — It&apos;s Free
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/pricing" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}